import "./CashFlowChart.css";

interface Month {
  label: string;
  income: number;
  expenses: number;
}

/**
 * Static demo figures — a farm year, not a straight line — and named as such
 * in the card's own note and `data-chart-status` (see the component below),
 * not quietly passed off as this tenant's own numbers (fc-508-live-kpis).
 * ADR 0009 §4 retired the "Illustrative" BADGE specifically — a state the
 * system doesn't actually know, since this is a fixed fixture, not a
 * computed status — while the prose note stays: that sentence is real
 * information a reader needs, not decoration.
 *
 * WHY THIS ONE STAYS FIXED RATHER THAN BEING DERIVED, unlike the KPI cards
 * above it on this same dashboard: `db/seed/0002_seed_demo.sql`'s Live Wall
 * seed is 14 entries spanning about eight days (days_ago 1–8), and this
 * screen has no endpoint that reaches further back than "now" anyway — the
 * live feed IS the whole history a fresh demo tenant has. Deriving a
 * seven-month chart from eight days of entries would mean either one real
 * bar next to six empty ones (worse booth material than an honest fixture —
 * a single-bar chart reads as broken, not as "new"), or synthesizing the
 * other six, which is exactly the invented-number problem this fix exists to
 * remove, just moved one level down. A chart that says "this is illustrative"
 * and means it beats one that quietly claims to be live and isn't.
 *
 * Raising the seed volume so this COULD be genuinely derived is a real
 * option, but a separate decision from this fix: it touches reset behaviour,
 * the scripted receipt demo's "no payment history yet" precondition, and the
 * amount thresholds other components key off (EntryRow's NOTABLE_AMOUNT) —
 * more than a labeling fix should carry this close to the date.
 *
 * The SHAPE stays deliberate either way: heavy spring input spend (seed,
 * fertilizer, fuel), thin summer, then the harvest spike in August. A farmer
 * reads this shape in a second and recognises their own year; a smooth
 * upward trend would read as stock art and quietly cost the credibility the
 * booth exists to build.
 */
const MONTHS: Month[] = [
  { label: "Feb", income: 18_400, expenses: 31_200 },
  { label: "Mar", income: 22_100, expenses: 58_900 },
  { label: "Apr", income: 26_800, expenses: 74_300 },
  { label: "May", income: 31_500, expenses: 46_100 },
  { label: "Jun", income: 29_200, expenses: 38_700 },
  { label: "Jul", income: 44_900, expenses: 35_400 },
  { label: "Aug", income: 96_300, expenses: 41_800 },
];

/**
 * The month the chart is ABOUT. Harvest income landing in August is the story a
 * farmer reads off this card, so that column gets a darker label and a callout
 * and the other six stay quiet. Emphasis is information here, not decoration.
 */
const HIGHLIGHT = "Aug";

// Fixed viewBox; the SVG scales to its container so this is layout maths, not
// pixels on anyone's screen.
const W = 560;
const H = 186;
const BASELINE = 152; // y of the axis
const PLOT_H = 142; // drawing height above the baseline
const GROUP_GAP = 16;
/** Fractions of full scale to draw gridlines at. */
const GRIDLINES = [0.25, 0.5, 0.75, 1];

/**
 * Cash flow, hand-rolled SVG.
 *
 * No charting library on purpose. This repo takes dependencies deliberately, and
 * a grouped bar chart is about forty lines of arithmetic — importing a charting
 * runtime to draw fourteen rectangles would be the larger cost, in bundle size at
 * a booth on cellular and in a dependency nobody audits.
 *
 * The first version drew flat bars on a bare baseline, which is a picture of the
 * data rather than a reading of it. Three things earn the space it takes:
 * GRIDLINES, so a bar has something to be measured against; GRADIENT fills, so
 * the bars have the same weight as the cards around them; and the harvest
 * CALLOUT, because "$96.3k" is the one number on this chart worth saying out
 * loud, and making the reader estimate it from a bar height wastes it.
 *
 * Income is leaf green and expenses gold: both brand colours, unmistakably
 * different at a distance, and together they read as the logo. Colour is NOT the
 * only channel — the legend, the axis labels and the aria-label all carry the
 * same information for anyone who cannot separate the two hues.
 *
 * `cashflow__note` below is the label half of "derive it, or label it" — see
 * the MONTHS doc above for why deriving isn't honestly possible from an
 * eight-day seeded ledger. Reads on screen, not just in a comment:
 * `data-chart-status="demo"` is the same machine-readable breadcrumb
 * KpiCard's `data-kpi-status` uses (rules/ui-breadcrumbs.md #1) — a fixed
 * fixture rather than "illustrative", which read as a claim about the DATA's
 * confidence rather than what this actually is: static, on purpose.
 */
export function CashFlowChart() {
  const peak = Math.max(...MONTHS.flatMap((m) => [m.income, m.expenses]));
  const groupW = W / MONTHS.length;
  const barW = (groupW - GROUP_GAP) / 2;

  const height = (value: number) => (value / peak) * PLOT_H;
  const y = (value: number) => BASELINE - height(value);

  return (
    <div className="cashflow" data-chart-status="demo">
      <div className="cashflow__head">
        <span className="cashflow__title-row">
          <h2 className="cashflow__title">Cash flow</h2>
        </span>
        <div className="cashflow__legend">
          <span className="cashflow__key cashflow__key--income">Income</span>
          <span className="cashflow__key cashflow__key--expense">Expenses</span>
        </div>
      </div>

      <svg
        className="cashflow__svg"
        viewBox={`0 0 ${W} ${H}`}
        /* preserveAspectRatio defaults to "xMidYMid meet", i.e. the chart scales
           UNIFORMLY. The previous version forced "none", which stretched the
           viewBox horizontally and took the text with it — month labels came out
           wider than they were tall at any card width but one. */
        role="img"
        aria-label={
          "Cash flow, February to August, a representative farm " +
          "year rather than this tenant's own entries. " +
          MONTHS.map(
            (m) =>
              `${m.label}: income ${Math.round(m.income / 1000)} thousand, ` +
              `expenses ${Math.round(m.expenses / 1000)} thousand`,
          ).join(". ")
        }
      >
        <defs>
          {/* Vertical gradients, light at the top. Flat fills read as a diagram;
              a gradient gives the bar a light source, which is the same light
              source the header horizon uses. Stop colours are tokens via CSS
              classes (stop-color, not the fill prop) rather than the hex
              literals this used to hardcode — ef-037b-2's token sweep. */}
          <linearGradient id="cashflow-income" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="cashflow__stop-income-start" />
            <stop offset="100%" className="cashflow__stop-income-end" />
          </linearGradient>
          <linearGradient id="cashflow-expense" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="cashflow__stop-expense-start" />
            <stop offset="100%" className="cashflow__stop-expense-end" />
          </linearGradient>
        </defs>

        {GRIDLINES.map((fraction) => (
          <line
            key={fraction}
            x1={0}
            x2={W}
            y1={BASELINE - PLOT_H * fraction}
            y2={BASELINE - PLOT_H * fraction}
            className="cashflow__gridline"
          />
        ))}

        <line x1={0} y1={BASELINE} x2={W} y2={BASELINE} className="cashflow__axis" />

        {MONTHS.map((m, i) => {
          const x = i * groupW + GROUP_GAP / 2;
          const highlighted = m.label === HIGHLIGHT;
          return (
            <g key={m.label}>
              <rect
                x={x}
                y={y(m.income)}
                width={barW}
                height={height(m.income)}
                rx={3}
                fill="url(#cashflow-income)"
              />
              <rect
                x={x + barW + 2}
                y={y(m.expenses)}
                width={barW}
                height={height(m.expenses)}
                rx={3}
                fill="url(#cashflow-expense)"
              />
              <text
                x={x + barW}
                y={172}
                textAnchor="middle"
                className={`cashflow__label${highlighted ? " cashflow__label--on" : ""}`}
              >
                {m.label}
              </text>
              {highlighted && (
                <text
                  x={x + barW / 2}
                  y={y(m.income) - 7}
                  textAnchor="middle"
                  className="cashflow__callout-amount"
                >
                  ${(m.income / 1000).toFixed(1)}k
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <p className="cashflow__note">
        A representative farm year, not this tenant's own entries — a new demo
        tenant has about a week of history so far. Harvest income lands in
        August; input costs peak in spring.
      </p>
    </div>
  );
}
